/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointAuthSchemeSelectionComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorPanelContentType, ActionsCreatorInstanceIds } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { IEndpointsEditorPanelCallback, IEndpointAuthSchemeSelectionPanelState, IServiceEndpointAuthenticationSchemeExtended } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";

import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { Button } from "VSSUI/Button";
import { EndpointsEditorPanelUtils } from "../Utils/EndpointsEditorPanelUtils";
import { ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { RecommendedBadge } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/RecommendedBadge";

export interface IEndpointAuthSchemeSelectionComponentProps extends IEndpointsEditorPanelCallback {
    selectedAuthSchemeIndex?: number;
    endpointType: ServiceEndpointType;
    hideBackButton?: boolean;
}

export interface IEndpointAuthSchemeSelectionComponentState {
    selectedAuthenticationSchemeIndex: number;
}

export class EndpointAuthSchemeSelectionComponent extends VssComponent<IEndpointAuthSchemeSelectionComponentProps, IEndpointAuthSchemeSelectionComponentState> {
    private _endpointsEditorActionsCreator: EndpointsEditorActionsCreator;

    constructor(props: IEndpointAuthSchemeSelectionComponentProps, context: IVssComponentContext) {
        super(props, context);

        this._endpointsEditorActionsCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>
            (EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);

        let selectedIndex: number = 0;
        this._authSchemes = EndpointsEditorPanelUtils.getExtendedAuthSchemesForEndpoint(context.pageContext, props.endpointType);
        if (this._authSchemes && this.props.selectedAuthSchemeIndex) {
            selectedIndex = (this.props.selectedAuthSchemeIndex > 0 && this.props.selectedAuthSchemeIndex < this._authSchemes.length) ?
                                this.props.selectedAuthSchemeIndex : 0;
        }

        this.state = {
            selectedAuthenticationSchemeIndex: selectedIndex
        };
    }
    public render(): React.ReactNode {
        if (this._authSchemes &&  this._authSchemes.length > 0) {
            return (
                <div className="connection-types-container connection-types-container-width">
                    <div className="custom-scrollbar v-scroll-auto">
                        <div className="connection-types-radio-group">
                            <RadioButtonGroup
                                text={Resources.AuthenticationPickerHeader}
                                onSelect={ this._onAuthSchemeSelected }
                                selectedButtonId={ this.state.selectedAuthenticationSchemeIndex.toString() }
                            >
                                {
                                this._authSchemes.map((authScheme, index) => {
                                        return this._getAuthSchemeCellContent(authScheme, index);
                                    })
                                }
                            </RadioButtonGroup>
                        </div>
                    </div>
                    { this._getFooterContent() }
                </div>
            );
        } else {
            return (
                <div>
                    <div className="connections-no-auth-scheme font-size-l">
                        { Resources.NoAuthSchemeFound }
                    </div>
                    { this._getFooterContent() }
                </div>
            );
        }
    }

    private _onAuthSchemeSelected = (authIndexString: string) => {
        let authIndex = parseInt(authIndexString);
        if (authIndex >= 0 && authIndex < this._authSchemes.length) {
            this.setState({ selectedAuthenticationSchemeIndex : authIndex});
        }
    }

    private _getAuthSchemeCellContent(authScheme: IServiceEndpointAuthenticationSchemeExtended, index: number): JSX.Element {
        let showBorder = (index !== (this._authSchemes.length - 1));
        let iconUrl = this.props.endpointType ? this.props.endpointType.iconUrl : "";
        return (
            <RadioButton className={showBorder ? "box-shadow-cell connection-types-list-cell" : "connection-types-list-cell"} key={index} id={index.toString()}>
                <div className="bolt-radio-button-label connection-types-item flex-row">
                    <img className="connection-types-icon" src={iconUrl} alt="" role="presentation" />
                    <span className="connection-types-name body-m">{authScheme.displayName}</span>
                    {this._getRecommendedTag(authScheme)}
                </div>
            </RadioButton>
        );
    }

    private _getRecommendedTag(authScheme: IServiceEndpointAuthenticationSchemeExtended): JSX.Element {
        return (<RecommendedBadge isRecommended={authScheme.isRecommended} className="auth-schemes-recommended-section"/>);
    }

    private _getFooterContent(): JSX.Element {
        return (
            <div className="connections-editor-panel-footer">
                <div className="connections-editor-panel-footer-left">
                    <Link
                        href={EndpointsEditorPanelUtils.getLearnMoreLinkForAuthSchemeSelectionPanel(this.props.endpointType.name)}
                        target="_blank"
                    >
                        { Resources.ChoosingConnectionTypeHelpText }
                    </Link>
                </div>
                <div className="connections-editor-panel-footer-right">
                    {
                        !this.props.hideBackButton &&
                        <Button
                            className="back-button"
                            text={Resources.BackButtonText}
                            onClick={() => { this._onBackButtonClicked(); }}
                        />
                    }
                    {
                        (this._authSchemes && this._authSchemes.length > 0) &&
                        <Button
                            primary
                            disabled={this.state.selectedAuthenticationSchemeIndex === null}
                            text={Resources.NextText}
                            onClick={() => { this._onNextButtonClicked(this.state.selectedAuthenticationSchemeIndex); }}
                        />
                    }

                </div>
            </div>
        );
    }

    private _onBackButtonClicked = () => {
        this.props.updateComponent({
            contentType: EndpointsEditorPanelContentType.ServiceEndpointTypes,
            previousContentType: EndpointsEditorPanelContentType.EndpointAuthSchemes,
            panelTitle: Resources.SelectEndpointTypesPanelTitle,
        });
    }

    private _onNextButtonClicked = (authIndex: number) => {
        if (authIndex >= 0 && authIndex < this._authSchemes.length) {
            const authScheme: IServiceEndpointAuthenticationSchemeExtended = this._authSchemes[authIndex];
            if (authScheme && authScheme.scheme) {
                this._endpointsEditorActionsCreator.initializeServiceEndpointDraftMode(authScheme.allowDraft);
                this._endpointsEditorActionsCreator.initializeServiceEndpointAuthenticationScheme(authScheme.scheme);
                if (authScheme.creationMode) {
                    this._endpointsEditorActionsCreator.initializeServiceEndpointCreationMode(authScheme.creationMode);
                }

                let contentType = EndpointsEditorPanelContentType.CreateForm;
                let panelTitle = authScheme.pageHeading;
                let panelDescription = authScheme.pageDescription;
                let authSchemeState: IEndpointAuthSchemeSelectionPanelState = { selectedAuthSchemeIndex: authIndex };

                this.props.updateComponent({
                    contentType: contentType,
                    previousContentType: EndpointsEditorPanelContentType.EndpointAuthSchemes,
                    panelTitle: panelTitle,
                    panelDescription: panelDescription,
                    authSchemeSelectionPanelState: authSchemeState
                });
            }
        }
    }

    private _authSchemes: IServiceEndpointAuthenticationSchemeExtended[];
}
